import React from 'react';

import {
	type Header,
	type HeaderGroup,
	type Table,
	type TableOptions,
} from '@tanstack/react-table';

import { cssMap } from '@atlaskit/css';

import { TR } from '../../../ui/primitives/tr';
import type { CharlieTableProps, TableType } from '../../charlie-table.types';
import type { ColumnVirtualizer } from '../../hooks/virtualization/use-charlie-column-virtualization';

import { CharlieTableHeader } from './charlie-table-header';
const styles = cssMap({
	row: {
		width: '100%',
		display: 'flex',
	},
});
interface CharlieTableHeadRowProps<T extends object> {
	headerGroup: HeaderGroup<T>;
	table: Table<T>;
	enableRowVirtualization: NonNullable<CharlieTableProps<T>['enableRowVirtualization']>;
	labels: NonNullable<CharlieTableProps<T>['labels']>;
	enableResizing: NonNullable<CharlieTableProps<T>['enableResizing']>;
	columnResizeMode?: CharlieTableProps<T>['columnResizeMode'];
	tableType: TableType;
	enableColumnReorder?: CharlieTableProps<T>['enableColumnReorder'];
	hasWidths: boolean;
	headerCellXcss?: CharlieTableProps<T>['headerCellXcss'];
	enableColumnActions?: CharlieTableProps<T>['enableColumnActions'];
	responsiveColumnSizing?: CharlieTableProps<T>['responsiveColumnSizing'];
	columnVirtualizer?: ColumnVirtualizer;
	columnActions?: CharlieTableProps<T>['columnActions'];
	onColumnSizingChangeRef?: TableOptions<T>['onColumnSizingChange'];
	onResizeHandleHoverChange?: (columnId: string | false) => void;
}

export const CharlieTableHeadRow = <T extends object>({
	headerGroup,
	table,
	enableRowVirtualization,
	labels,
	enableResizing,
	columnResizeMode = 'onChange',
	tableType,
	enableColumnReorder,
	hasWidths,
	headerCellXcss,
	enableColumnActions,
	responsiveColumnSizing,
	columnVirtualizer,
	columnActions,
	onColumnSizingChangeRef,
	onResizeHandleHoverChange,
}: CharlieTableHeadRowProps<T>): React.JSX.Element => {
	const { virtualColumns, virtualPaddingLeft, virtualPaddingRight } = columnVirtualizer ?? {};

	const headers = virtualColumns ?? headerGroup.headers;

	return (
		<TR key={headerGroup.id} xcss={styles.row}>
			{virtualPaddingLeft ? (
				//eslint-disable-next-line  @atlaskit/ui-styling-standard/enforce-style-prop
				<th style={{ display: 'flex', width: virtualPaddingLeft }} />
			) : null}
			{headers.map((headerOrVirtualHeader, idx) => {
				const header = virtualColumns
					? headerGroup.headers[headerOrVirtualHeader.index]
					: (headerOrVirtualHeader as Header<T, unknown>);
				return (
					<CharlieTableHeader<T>
						enableRowVirtualization={enableRowVirtualization}
						key={header.id}
						labels={labels}
						charlieTableColumn={header}
						index={idx}
						enableResizing={enableResizing && header.column.getCanResize()}
						columnResizeMode={columnResizeMode}
						charlieTableState={table}
						tableType={tableType}
						enableColumnReorder={enableColumnReorder}
						hasWidths={hasWidths}
						xcss={headerCellXcss}
						enableColumnActions={enableColumnActions}
						responsiveColumnSizing={responsiveColumnSizing}
						columnActions={columnActions}
						onColumnSizingChangeRef={onColumnSizingChangeRef}
						onResizeHandleHoverChange={onResizeHandleHoverChange}
					/>
				);
			})}

			{virtualPaddingRight ? (
				//eslint-disable-next-line  @atlaskit/ui-styling-standard/enforce-style-prop
				<th style={{ display: 'flex', width: virtualPaddingRight }} />
			) : null}
		</TR>
	);
};
